import { useEffect, useState } from "react";
import { formatDateTime } from "../lib/dateFormat";
import { formatReportValue } from "../lib/dateFormat";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api, API_BASE } from "../lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, ChevronRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

const TYPE_OPTS = ["string", "number", "date", "boolean"];

export default function ExcelUploads() {
  useAuth();
  const nav = useNavigate();

  const [parsing, setParsing] = useState(false);
  const [parseRes, setParseRes] = useState(null);  // {parse_token, filename, sheets:[...]}
  const [activeSheet, setActiveSheet] = useState(null);
  const [columns, setColumns] = useState([]); // editable [{id,label,detected_type,type,include}]
  const [datasetName, setDatasetName] = useState("");
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState([]);

  const loadExisting = async () => {
    const r = await api.get("/datasets");
    setExisting((r.data.datasets || []).filter((d) => d.kind === "excel"));
  };
  useEffect(() => { loadExisting(); }, []);

  const onPick = async (e) => {
    setError(""); setCommitted(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/uploads/excel/parse`, {
        method: "POST", body: fd, headers: {
          Authorization: `Bearer ${localStorage.getItem("camaroo_token")}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Parse failed");
      setParseRes(data);
      setActiveSheet(data.sheets[0]?.name || null);
      const cols = (data.sheets[0]?.columns || []).map((c) => ({ ...c, type: c.detected_type, include: true }));
      setColumns(cols);
      setDatasetName(file.name.replace(/\.[^.]+$/, ""));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setParsing(false);
    }
  };

  const switchSheet = (name) => {
    const sh = parseRes.sheets.find((s) => s.name === name);
    setActiveSheet(name);
    setColumns((sh?.columns || []).map((c) => ({ ...c, type: c.detected_type, include: true })));
  };

  const setColField = (idx, patch) => setColumns((s) => s.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const commit = async () => {
    setCommitting(true); setError("");
    try {
      const body = {
        parse_token: parseRes.parse_token,
        sheet: activeSheet,
        dataset_name: datasetName,
        columns: columns.map(({ id, label, type, include }) => ({ id, label, type, include })),
      };
      const r = await api.post("/uploads/excel/commit", body);
      setCommitted(r.data);
      loadExisting();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setCommitting(false);
    }
  };

  const activeSheetData = parseRes?.sheets?.find((s) => s.name === activeSheet);

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Excel Uploads</h1>
            <p>Upload client Excel/CSV files and use them as datasets in Reports, KPI Center, and Dashboard Builder.</p>
          </div>
        </div>

        <div className="space-y-5">

          {/* Step 1: pick file */}
          <div className="card-flat p-5" data-testid="excel-upload-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="section-title">Upload a file</div>
                <div className="section-sub">.xlsx, .xls or .csv up to 30 MB. Each sheet becomes a selectable dataset source.</div>
              </div>
              <label className="btn btn-primary cursor-pointer" data-testid="excel-pick-file">
                <Upload size={12} className="mr-1" /> Choose file
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onPick} data-testid="excel-file-input" />
              </label>
            </div>
            {parsing && (
              <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
                <Loader2 size={12} className="animate-spin" /> Parsing file…
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "#991b1b" }} data-testid="excel-error">
                <AlertCircle size={12} /> {error}
              </div>
            )}
            {committed && (
              <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "#14532d" }} data-testid="excel-success">
                <CheckCircle2 size={14} />
                <span>Dataset created: <strong>{datasetName}</strong> · {committed.rows.toLocaleString()} rows
                  {committed.load_errors > 0 && (
                    <span style={{ color: "#854d0e" }}> · {committed.load_errors} cell coercion errors</span>
                  )}
                </span>
                <button className="btn btn-outline text-[11px] ml-2" onClick={() => nav("/datasets")} data-testid="excel-open-datasets">Open in Datasets</button>
              </div>
            )}
          </div>

          {/* Step 2: configure */}
          {parseRes && !committed && (
            <div className="card-flat p-5" data-testid="excel-config-card">
              <div className="flex items-center gap-2 mb-3 text-[12.5px]">
                <FileSpreadsheet size={14} />
                <span className="font-mono">{parseRes.filename}</span>
                <span style={{ color: "var(--c-text-muted)" }}>· {parseRes.sheets.length} sheet(s)</span>
              </div>

              {parseRes.sheets.length > 1 && (
                <div className="flex flex-wrap gap-1 mb-3" data-testid="excel-sheet-tabs">
                  {parseRes.sheets.map((s) => (
                    <button
                      key={s.name}
                      className={`text-[12px] px-2.5 py-1 rounded ${activeSheet === s.name ? "bg-slate-900 text-white" : "bg-slate-100"}`}
                      onClick={() => switchSheet(s.name)}
                      data-testid={`excel-sheet-${s.name}`}
                    >{s.name} · {s.row_count} rows</button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Dataset name</label>
                  <input className="input" value={datasetName} onChange={(e) => setDatasetName(e.target.value)} data-testid="excel-dataset-name" />
                </div>
                <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                  Rows: <strong>{activeSheetData?.row_count?.toLocaleString() || 0}</strong>
                  · Columns: <strong>{columns.filter((c) => c.include).length}/{columns.length}</strong>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto border rounded" style={{ borderColor: "var(--c-border)" }}>
                <table className="tbl text-[12px]" data-testid="excel-columns-table">
                  <thead>
                    <tr><th>Use</th><th>Source header</th><th>Field name</th><th>Detected</th><th>Type</th><th>Sample</th></tr>
                  </thead>
                  <tbody>
                    {columns.map((c, i) => (
                      <tr key={c.id} data-testid={`excel-col-${c.id}`}>
                        <td><input type="checkbox" checked={c.include} onChange={(e) => setColField(i, { include: e.target.checked })} data-testid={`excel-col-include-${c.id}`} /></td>
                        <td className="font-mono">{c.label}</td>
                        <td className="font-mono"><input className="input" value={c.label} onChange={(e) => setColField(i, { label: e.target.value })} /></td>
                        <td><span className="pill" style={{ background: "#eef2ff", color: "#3730a3" }}>{c.detected_type}</span></td>
                        <td>
                          <select className="select" value={c.type} onChange={(e) => setColField(i, { type: e.target.value })} data-testid={`excel-col-type-${c.id}`}>
                            {TYPE_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td className="font-mono text-[11px]" style={{ color: "var(--c-text-muted)" }}>{(c.sample || []).slice(0, 3).join(" · ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Preview */}
              <div className="mt-4">
                <div className="text-[12px] mb-1" style={{ color: "var(--c-text-muted)" }}>Preview (first 20 rows)</div>
                <div className="overflow-x-auto border rounded max-h-[260px]" style={{ borderColor: "var(--c-border)" }}>
                  <table className="tbl text-[11.5px]" data-testid="excel-preview-table">
                    <thead>
                      <tr>{columns.filter((c) => c.include).map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(activeSheetData?.preview_rows || []).map((r, i) => (
                        <tr key={i}>
                          {columns.filter((c) => c.include).map((c) => (
                            <td key={c.id} className="font-mono">{r[c.id] == null ? "—" : String(r[c.id])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 justify-end">
                <button className="btn btn-ghost" onClick={() => { setParseRes(null); setColumns([]); setDatasetName(""); }} data-testid="excel-cancel">Cancel</button>
                <button className="btn btn-primary" onClick={commit} disabled={committing || !datasetName} data-testid="excel-commit">
                  {committing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Create dataset
                </button>
              </div>
            </div>
          )}

          {/* Existing uploaded datasets */}
          <div className="card-flat" data-testid="excel-existing">
            <div className="px-4 py-2.5 border-b text-[12.5px] font-medium" style={{ borderColor: "var(--c-border)" }}>Your uploaded datasets ({existing.length})</div>
            <table className="tbl text-[12.5px]">
              <thead><tr><th>Name</th><th>Source file</th><th>Sheet</th><th>Rows</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {existing.map((d) => (
                  <ExistingRow key={d.id} d={d} onReloaded={loadExisting} />
                ))}
                {existing.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>No uploads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}


function ExistingRow({ d, onReloaded }) {
  const [mode, setMode] = useState("");
  const [name, setName] = useState(d.name || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = d.source_meta || {};

  const view = async () => {
    setMode("view"); setBusy(true); setError("");
    try {
      const r = await api.post("/widget/data", {
        widget: { id: "_excel_preview", type: "table", dataset_id: d.id, limit: 50 },
        filters: [],
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!name.trim()) return setError("Dataset name is required");
    setBusy(true); setError("");
    try {
      await api.put(`/datasets/${d.id}`, {
        connection_id: d.connection_id, name: name.trim(),
        description: d.description || "", base_table: d.base_table || "",
        joins: d.joins || [], fields: d.fields || [],
        filters: d.filters || [], custom_sql: d.custom_sql || "",
      });
      setMode(""); onReloaded();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError("");
    try {
      await api.delete(`/datasets/${d.id}`);
      setMode(""); onReloaded();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <>
      <tr data-testid={`excel-existing-${d.id}`}>
        <td><div className="font-medium">{d.name}</div><div className="text-[10.5px] font-mono" style={{color:"var(--c-text-muted)"}}>{d.id}</div></td>
        <td className="font-mono">{meta.filename || "—"}</td>
        <td className="font-mono">{meta.sheet || "—"}</td>
        <td className="numeric">{(meta.row_count || 0).toLocaleString()}</td>
        <td>{meta.uploaded_at ? formatDateTime(meta.uploaded_at) : "—"}</td>
        <td className="text-right whitespace-nowrap">
          <button className="btn btn-outline text-[11px] mr-1" onClick={view}>View</button>
          <button className="btn btn-outline text-[11px] mr-1" onClick={() => {setName(d.name || "");setError("");setMode("edit");}}>Edit</button>
          <button className="btn btn-outline text-[11px]" onClick={() => {setError("");setMode("delete");}}>Delete</button>
        </td>
      </tr>
      {mode && <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40 p-5"><div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] overflow-auto p-5 relative"><button className="absolute top-3 right-3 btn btn-ghost" onClick={()=>setMode("")}>✕</button>
        {mode === "view" && <><h3 className="font-semibold mb-3">{d.name}</h3>{busy ? <Loader2 className="animate-spin" /> : error ? <div className="text-red-700">{error}</div> : <table className="tbl text-[11px]"><tbody>{(data?.rows || []).slice(0,50).map((r,i)=><tr key={i}>{Object.entries(r).map(([column, v])=><td key={column}>{String(formatReportValue(v, column) ?? "")}</td>)}</tr>)}</tbody></table>}</>}
        {mode === "edit" && <><h3 className="font-semibold mb-3">Edit dataset</h3><input className="input w-full" value={name} onChange={e=>setName(e.target.value)} />{error && <div className="text-red-700 mt-2">{error}</div>}<div className="mt-4 flex justify-end gap-2"><button className="btn btn-ghost" onClick={()=>setMode("")}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save"}</button></div></>}
        {mode === "delete" && <><h3 className="font-semibold mb-3">Delete dataset?</h3><p>Delete <strong>{d.name}</strong> and its uploaded rows?</p>{error && <div className="text-red-700 mt-2">{error}</div>}<div className="mt-4 flex justify-end gap-2"><button className="btn btn-ghost" onClick={()=>setMode("")}>Cancel</button><button className="btn btn-primary" onClick={remove} disabled={busy}>{busy ? "Deleting..." : "Delete"}</button></div></>}
      </div></div>}
    </>
  );
}
